import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FaEye, FaExchangeAlt, FaPlus, FaUser } from 'react-icons/fa';
import baseURL from '../../baseURL';
import { resolvePledgePhotoUrl } from '../../utils/pledgePhotoUrl';
import { DHARA_OPTIONS, formatDhara } from '../../constants/dhara';
import { fetchTransferToAPreview, transferPledgesToA } from '../../utils/pledgeApi';
import LedgerMaster from './master/LedgerMaster';

function emptyForm() {
  return {
    customer: {
      code: '',
      firstName: '',
      lastName: '',
      mobileNo: '',
      address: '',
      occupation: '',
      village: '',
      nomineeName: ''
    },
    loanDetails: {
      principalAmount: '',
      duration: '12',
      rateOfInterest: '',
      dhara: '30_day',
      paidExcess: false
    },
    disbursementAmount: ''
  };
}

function partyDisplayName(customer) {
  return [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim() || 'Party';
}

function ledgerToCustomer(ledger) {
  if (!ledger) return emptyForm().customer;
  return {
    code: ledger.code || '',
    firstName: ledger.firstName || '',
    lastName: ledger.lastName || '',
    mobileNo: ledger.mobileNo || '',
    address: ledger.address || '',
    occupation: ledger.occupation || '',
    village: ledger.village || '',
    nomineeName: ledger.nomineeName || ''
  };
}

function resolveLedgerPhoto(photos) {
  const stored = photos?.clientPhoto;
  return stored ? resolvePledgePhotoUrl(stored) : null;
}

function formFromPreview(data) {
  return {
    customer: {
      code: data.customer?.code || '',
      firstName: data.customer?.firstName || '',
      lastName: data.customer?.lastName || '',
      mobileNo: data.customer?.mobileNo || '',
      address: data.customer?.address || '',
      occupation: data.customer?.occupation || '',
      village: data.customer?.village || '',
      nomineeName: data.customer?.nomineeName || ''
    },
    loanDetails: {
      principalAmount: String(data.loanDetails?.principalAmount ?? ''),
      duration: String(data.loanDetails?.duration ?? 12),
      rateOfInterest: String(data.loanDetails?.rateOfInterest ?? ''),
      dhara: data.loanDetails?.dhara || '30_day',
      paidExcess: !!data.loanDetails?.paidExcess
    },
    disbursementAmount: String(data.disbursementAmount ?? data.loanDetails?.principalAmount ?? '')
  };
}

const TransferToADialog = ({ open, pledgeIds, onClose, onSuccess }) => {
  const [queueIndex, setQueueIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [refreshingCustomer, setRefreshingCustomer] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addCustomerModalInstance, setAddCustomerModalInstance] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);

  const currentPledgeId = pledgeIds[queueIndex];
  const total = pledgeIds.length;
  const articlePhotoUrl = preview?.articlePhoto ? resolvePledgePhotoUrl(preview.articlePhoto) : null;
  const partyPhotoUrl =
    resolveLedgerPhoto(selectedLedger?.photos) ||
    (preview?.clientPhoto ? resolvePledgePhotoUrl(preview.clientPhoto) : null);

  const fetchCustomers = useCallback(async (search = '') => {
    setLoadingCustomers(true);
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ type: 'Customer' });
      if (search.trim()) qs.set('search', search.trim());
      const res = await fetch(`${baseURL}/ledgers?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data.data) ? data.data : data.data?.customers || [];
      setCustomers(list);
      return list;
    } catch {
      setCustomers([]);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setQueueIndex(0);
      setPreview(null);
      setForm(emptyForm());
      setError('');
      setSelectedLedger(null);
      setSearchTerm('');
      setDropdownOpen(false);
      setShowAddCustomerModal(false);
      fetchCustomers('');
    }
  }, [open, pledgeIds, fetchCustomers]);

  useEffect(() => {
    if (!showAddCustomerModal) {
      setAddCustomerModalInstance((prev) => prev + 1);
    }
  }, [showAddCustomerModal]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fetchCustomers(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [open, searchTerm, fetchCustomers]);

  useEffect(() => {
    if (!open || !currentPledgeId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setSelectedLedger(null);
      try {
        const data = await fetchTransferToAPreview(currentPledgeId);
        if (cancelled) return;
        setPreview(data);
        setForm(formFromPreview(data));

        const code = data.customer?.code;
        if (code) {
          const list = await fetchCustomers('');
          if (cancelled) return;
          const match = list.find((c) => String(c.code) === String(code));
          const ledger =
            match ||
            ({
              code,
              firstName: data.customer?.firstName,
              lastName: data.customer?.lastName,
              mobileNo: data.customer?.mobileNo,
              village: data.customer?.village,
              occupation: data.customer?.occupation,
              address: data.customer?.address,
              nomineeName: data.customer?.nomineeName
            });
          setSelectedLedger(ledger);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load transfer preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, currentPledgeId, fetchCustomers]);

  const selectCustomer = async (customer) => {
    if (!customer) return;
    setSelectedLedger(customer);
    setDropdownOpen(false);
    setSearchTerm('');
    const customerFields = ledgerToCustomer(customer);
    setForm((prev) => ({ ...prev, customer: customerFields }));

    if (!currentPledgeId || !customer.code) return;

    setRefreshingCustomer(true);
    setError('');
    try {
      const data = await fetchTransferToAPreview(currentPledgeId, customer.code);
      setPreview((prev) => ({ ...prev, ...data }));
      setForm((prev) => ({
        ...prev,
        customer: { ...customerFields, ...(data.customer || {}) },
        loanDetails: {
          ...prev.loanDetails,
          rateOfInterest: String(
            data.loanDetails?.rateOfInterest ?? prev.loanDetails.rateOfInterest
          ),
          dhara: data.loanDetails?.dhara || prev.loanDetails.dhara
        }
      }));
    } catch (err) {
      setError(err.message || 'Could not load VPS A preview for selected customer');
    } finally {
      setRefreshingCustomer(false);
    }
  };

  const handleAddNewCustomer = () => {
    setDropdownOpen(false);
    setShowAddCustomerModal(true);
  };

  const handleCustomerCreated = async (customer) => {
    const c = customer?.data || customer;
    if (!c) return;
    setShowAddCustomerModal(false);
    setCustomers((prev) => {
      const exists = prev.some((x) => String(x.code) === String(c.code));
      return exists ? prev : [c, ...prev];
    });
    await selectCustomer(c);
  };

  const updateLoan = (field, value) => {
    setForm((prev) => ({
      ...prev,
      loanDetails: { ...prev.loanDetails, [field]: value }
    }));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        transfers: [
          {
            pledgeId: currentPledgeId,
            customer: form.customer,
            loanDetails: {
              ...form.loanDetails,
              principalAmount: parseFloat(form.loanDetails.principalAmount) || 0,
              rateOfInterest: parseFloat(form.loanDetails.rateOfInterest) || 0,
              duration: parseInt(form.loanDetails.duration, 10) || 12,
              disbursementAmount: parseFloat(form.disbursementAmount) || 0
            },
            disbursementAmount: parseFloat(form.disbursementAmount) || 0
          }
        ]
      };
      const data = await transferPledgesToA(payload);
      const result = (data.results || [])[0];
      if (!result?.success) {
        throw new Error(result?.error || 'Transfer failed');
      }

      if (queueIndex < total - 1) {
        setQueueIndex((i) => i + 1);
        setPreview(null);
        setForm(emptyForm());
        setSelectedLedger(null);
        onSuccess?.([result], { moreRemaining: true });
      } else {
        onSuccess?.(data.results || [result], { done: true });
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setQueueIndex(0);
    setPreview(null);
    setForm(emptyForm());
    setSelectedLedger(null);
    setSearchTerm('');
    setShowAddCustomerModal(false);
    setError('');
    onClose();
  };

  const principal = parseFloat(form.loanDetails.principalAmount) || 0;
  const paid = parseFloat(form.disbursementAmount) || 0;

  return (
    <>
      <Modal show={open} onHide={handleClose} size="xl" centered backdrop="static" scrollable={false}>
        <Modal.Header closeButton className="py-2 px-3 bg-light">
          <Modal.Title className="d-flex align-items-center gap-2 fs-5 mb-0">
            <FaExchangeAlt className="text-primary" />
            Transfer to VPS A
            {preview?.bTicketNo && (
              <Badge bg="secondary" className="fw-normal">
                B ticket {preview.bTicketNo}
                {total > 1 ? ` · ${queueIndex + 1}/${total}` : ''}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
              {preview?.aPreviewMessage && !preview?.aCustomerFound && (
                <Alert variant="warning" className="py-2 mb-3 d-flex justify-content-between align-items-center">
                  <span>{preview.aPreviewMessage}</span>
                  <Button size="sm" variant="outline-warning" onClick={handleAddNewCustomer}>
                    Add customer
                  </Button>
                </Alert>
              )}

              <Row className="g-3">
                <Col lg={5}>
                  <Card className="h-100 border shadow-sm">
                    <Card.Header className="bg-white py-2 fw-semibold">Customer on VPS A</Card.Header>
                    <Card.Body className="py-3">
                      <Form.Group className="mb-3">
                        <div className="d-flex align-items-start gap-2">
                          <Dropdown
                            className="flex-grow-1"
                            show={dropdownOpen}
                            onToggle={(isOpen) => setDropdownOpen(isOpen)}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              className="w-100 text-start text-truncate d-flex align-items-center gap-2"
                            >
                              {partyPhotoUrl ? (
                                <img
                                  src={partyPhotoUrl}
                                  alt=""
                                  style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <FaUser className="text-muted" />
                              )}
                              <span className="text-truncate">
                                {selectedLedger
                                  ? `${selectedLedger.firstName || ''} ${selectedLedger.lastName || ''}`.trim() ||
                                    selectedLedger.code
                                  : 'Select customer'}
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              className="w-100 p-0"
                              style={{ minWidth: '100%', maxHeight: '280px', overflowY: 'auto' }}
                            >
                              <div className="p-2 border-bottom bg-light sticky-top">
                                <div className="d-flex gap-2">
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Search name, code, mobile..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && customers.length > 0) {
                                        e.preventDefault();
                                        selectCustomer(customers[0]);
                                      }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleAddNewCustomer();
                                    }}
                                  >
                                    <FaPlus className="me-1" />
                                    Add
                                  </Button>
                                </div>
                                {loadingCustomers && (
                                  <div className="text-center mt-2">
                                    <Spinner animation="border" size="sm" />
                                  </div>
                                )}
                              </div>
                              {customers.length === 0 && !loadingCustomers ? (
                                <Dropdown.Item disabled className="text-muted small">
                                  No customers — click Add
                                </Dropdown.Item>
                              ) : (
                                customers.map((customer) => {
                                  const photo = resolveLedgerPhoto(customer.photos);
                                  return (
                                    <Dropdown.Item
                                      key={customer.code || customer._id}
                                      onClick={() => selectCustomer(customer)}
                                      className="py-2 border-bottom"
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        {photo ? (
                                          <img
                                            src={photo}
                                            alt=""
                                            style={{
                                              width: 40,
                                              height: 40,
                                              borderRadius: '6px',
                                              objectFit: 'cover',
                                              flexShrink: 0
                                            }}
                                          />
                                        ) : (
                                          <div
                                            className="bg-secondary text-white d-flex align-items-center justify-content-center"
                                            style={{
                                              width: 40,
                                              height: 40,
                                              borderRadius: '6px',
                                              fontSize: 14,
                                              flexShrink: 0
                                            }}
                                          >
                                            {(customer.firstName?.[0] || '') + (customer.lastName?.[0] || '')}
                                          </div>
                                        )}
                                        <div className="min-w-0">
                                          <div className="fw-semibold text-truncate">
                                            {customer.firstName} {customer.lastName}
                                          </div>
                                          <div className="small text-muted text-truncate">
                                            <Badge bg="secondary" className="me-1">
                                              {customer.code}
                                            </Badge>
                                            {customer.mobileNo}
                                            {customer.village ? ` · ${customer.village}` : ''}
                                          </div>
                                        </div>
                                      </div>
                                    </Dropdown.Item>
                                  );
                                })
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                          {partyPhotoUrl && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title="View photo"
                              onClick={() => setPhotoPreview(partyPhotoUrl)}
                              style={{ height: 38 }}
                            >
                              <FaEye />
                            </Button>
                          )}
                        </div>
                      </Form.Group>

                      {selectedLedger && (
                        <div className="bg-light rounded p-2 small">
                          {refreshingCustomer && (
                            <div className="mb-1">
                              <Spinner animation="border" size="sm" className="me-1" />
                              Updating rate…
                            </div>
                          )}
                          <div>
                            <strong>Code:</strong> {form.customer.code || '—'}
                          </div>
                          <div>
                            <strong>Mobile:</strong> {form.customer.mobileNo || '—'}
                          </div>
                          <div>
                            <strong>Village:</strong> {form.customer.village || '—'}
                          </div>
                          {form.customer.address && (
                            <div className="text-muted mt-1" style={{ lineHeight: 1.35 }}>
                              {form.customer.address}
                            </div>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card className="h-100 border shadow-sm">
                    <Card.Header className="bg-white py-2 fw-semibold">Loan on VPS A</Card.Header>
                    <Card.Body className="py-3">
                      <Row className="g-2">
                        <Col xs={6} md={3}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Principal (₹)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={form.loanDetails.principalAmount}
                              onChange={(e) => updateLoan('principalAmount', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={3}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Rate %</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={form.loanDetails.rateOfInterest}
                              onChange={(e) => updateLoan('rateOfInterest', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={3}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Dhara</Form.Label>
                            <Form.Select
                              size="sm"
                              value={form.loanDetails.dhara}
                              onChange={(e) => updateLoan('dhara', e.target.value)}
                            >
                              {DHARA_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={3}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Months</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={form.loanDetails.duration}
                              onChange={(e) => updateLoan('duration', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={4}>
                          <Form.Group>
                            <Form.Label className="small mb-1">Cash out (₹)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={form.disbursementAmount}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, disbursementAmount: e.target.value }))
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="bg-light rounded p-2 mt-3 small">
                        <div className="d-flex flex-wrap gap-3">
                          <span>
                            <strong>Dr Sundry Debtors:</strong> ₹{principal.toLocaleString('en-IN')}
                          </span>
                          <span>
                            <strong>Cr Cash:</strong> ₹{paid.toLocaleString('en-IN')}
                          </span>
                          <span className="text-muted">Dhara: {formatDhara(form.loanDetails.dhara)}</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {preview?.articles?.length > 0 && (
                <Card className="mt-3 border shadow-sm">
                  <Card.Header className="bg-white py-2 d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Articles</span>
                    {articlePhotoUrl && (
                      <img
                        src={articlePhotoUrl}
                        alt="Article"
                        style={{
                          width: 36,
                          height: 36,
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: '1px solid #dee2e6'
                        }}
                      />
                    )}
                  </Card.Header>
                  <Table responsive size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Value</th>
                        <th className="text-end">Gross</th>
                        <th className="text-end">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.articles.map((a, idx) => (
                        <tr key={idx}>
                          <td>{a.description || a.articleTitle || '—'}</td>
                          <td className="text-end">₹{Number(a.valueTotal || 0).toLocaleString('en-IN')}</td>
                          <td className="text-end">{a.grossWeight ?? '—'} g</td>
                          <td className="text-end">{a.netWeight ?? '—'} g</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 bg-light">
          <small className="text-muted me-auto d-none d-md-inline">
            VPS B pledge stays · new ticket on A
          </small>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || submitting || !preview || !selectedLedger}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Transferring…
              </>
            ) : (
              <>
                <FaExchangeAlt className="me-2" />
                Confirm transfer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!photoPreview} onHide={() => setPhotoPreview(null)} centered size="sm">
        <Modal.Body className="p-2 text-center">
          {photoPreview && (
            <img src={photoPreview} alt="Customer" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8 }} />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        size="lg"
        scrollable
        backdrop="static"
        style={{ zIndex: 1060 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LedgerMaster
            key={addCustomerModalInstance}
            isModal
            onClose={() => setShowAddCustomerModal(false)}
            onCustomerCreated={handleCustomerCreated}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TransferToADialog;
